import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import {
  ChartComponentProps,
  getBaseOption,
  getChartNumericFields,
  getDisplayName,
  buildCategoryAxis,
  buildValueAxis,
  formatTooltipValue,
  getChartValueStats,
  formatTooltipStatsLine,
  ECHARTS_OPTS,
} from './chartConfig';

export const LineChart: React.FC<ChartComponentProps> = ({ chart, height = 320, colors, presentation }) => {
  const isArea = chart.type === 'area';

  const option = useMemo((): EChartsOption => {
    const data = chart.data || [];
    if (data.length === 0) return {};

    const fields = getChartNumericFields(chart, data);
    const base = getBaseOption(colors);
    const xAxis = { ...(buildCategoryAxis(chart, data) as Record<string, unknown>) };
    const yAxis = { ...(buildValueAxis(chart, fields) as Record<string, unknown>) };
    // Range slider is intentionally disabled for now.
    // Keep the original condition here so it can be restored later if needed.
    // const showRangeSlider = !!presentation?.enableRangeSlider && data.length > 24;

    if (presentation?.gridOverrides) {
      base.grid = {
        ...(base.grid as Record<string, unknown>),
        ...(presentation.gridOverrides as Record<string, unknown>),
      };
    }

    // Preserve the extra bottom grid spacing required by the range slider, but do not apply it for now.
    // if (showRangeSlider) {
    //   base.grid = {
    //     ...(base.grid as Record<string, unknown>),
    //     bottom: 96,
    //   };
    // }

    if (presentation?.hideXAxisLabels) {
      xAxis.axisLabel = { ...(xAxis.axisLabel as Record<string, unknown>), show: false };
      xAxis.axisTick = { ...(xAxis.axisTick as Record<string, unknown>), show: false };
      xAxis.name = presentation.hideXAxisName ? '' : xAxis.name;
    }

    if (presentation?.hideYAxisLabels) {
      yAxis.axisLabel = { ...(yAxis.axisLabel as Record<string, unknown>), show: false };
      yAxis.axisTick = { ...(yAxis.axisTick as Record<string, unknown>), show: false };
      yAxis.name = presentation.hideYAxisName ? '' : yAxis.name;
    }

    const series: EChartsOption['series'] = fields.map((field) => ({
      name: getDisplayName(chart, field),
      type: 'line' as const,
      data: data.map(d => d[field] ?? d.value ?? 0),
      smooth: true,
      symbol: 'circle',
      symbolSize: 4,
      showSymbol: data.length <= 30,
      areaStyle: isArea ? { opacity: 0.15 } : undefined,
      emphasis: { focus: 'series' as const },
    }));

    return {
      ...base,
      xAxis,
      yAxis,
      series,
      tooltip: {
        ...(base.tooltip as object),
        trigger: 'axis',
        formatter: (params: unknown) => {
          const items = Array.isArray(params) ? params : [params];
          const label = (items[0] as { name?: string })?.name ?? '';
          let html = `<div style="margin-bottom:4px;font-size:12px;color:#F7F3EE">${label}</div>`;
          for (const item of items) {
            const i = item as { marker?: string; seriesName?: string; value?: number };
            html += `<div style="display:flex;align-items:center;gap:6px;font-size:12px">${i.marker}<span>${i.seriesName}</span><span style="margin-left:auto">${formatTooltipValue(i.value)}</span></div>`;
          }
          const stats = getChartValueStats(data, fields);
          html += `<div style="margin-top:6px;padding-top:6px;border-top:1px solid rgba(247,243,238,.22);font-size:11px;color:#D9CFC4">${formatTooltipStatsLine(stats)}</div>`;
          return html;
        },
      },
      // Range slider intentionally not rendered for now.
      // dataZoom: showRangeSlider ? [
      //   {
      //     type: 'inside',
      //     xAxisIndex: 0,
      //     filterMode: 'none',
      //   },
      //   {
      //     type: 'slider',
      //     xAxisIndex: 0,
      //     filterMode: 'none',
      //     bottom: 20,
      //     height: 20,
      //     borderColor: '#EDE6E1',
      //     fillerColor: 'rgba(141, 49, 101, 0.16)',
      //     backgroundColor: 'rgba(148, 163, 184, 0.12)',
      //     handleSize: '85%',
      //     moveHandleSize: 8,
      //     start: data.length > 24 ? 0 : 0,
      //     end: data.length > 24 ? 45 : 100,
      //   },
      // ] : undefined,
      legend: fields.length > 1 ? base.legend : { show: false },
    };
  }, [chart, colors, isArea, presentation]);

  return <ReactECharts option={option} style={{ height }} notMerge lazyUpdate opts={ECHARTS_OPTS} />;
};
